import {
  rodsCalcPositionMethod,
  rodsCalcIsSpaceEnough,
  rodsCalcMaxPosition,
  rodsCalcMinPosition,
} from './_utils/rods';

import { polstCalcIsSpaceEnough } from './_utils/polst';

export const SIZE_GAP = 3;

export const calcRealHeight = value => Math.ceil(value / SIZE_GAP) * SIZE_GAP;

const LEDsMethod = (maxSpace, item) => {
  const { position } = item;

  const [, max] = position;

  return {
    ...maxSpace,
    position: max,
  };
};

function tShelfMethod(maxSpace, item, list) {
  const prevItem = list[maxSpace.indexElement];
  if (!prevItem) {
    return maxSpace;
  }

  if (prevItem.slug.includes('_polst_')) {
    return {
      ...maxSpace,
      position: maxSpace.position - prevItem.protective_field,
    };
  }

  return maxSpace;
}

const ledPosition = (maxSpace, item, list) => {
  const findItem = list.find(el => el.slug === item.slug);
  if (findItem) {
    return false;
  }
  return true;
};

function CalcMinimalPosition(slug) {
  if (slug.includes('_dr_')) {
    return rodsCalcMinPosition;
  }

  if (slug.includes('_szu1sdr_')) {
    return rodsCalcMinPosition;
  }

  if (slug.includes('_al_bi4k')) {
    return ledPosition;
  }

  return function defaultCalcMin(maxSpace, item, list) {
    const { position, protective_field, dimensions } = item;
    const [min] = position;
    const itemHeight = protective_field + dimensions.y;

    if (maxSpace.position <= min) {
      const prevItem = list[maxSpace.indexElement];

      if (prevItem && (prevItem.slug.includes('_dr_') || prevItem.slug.includes('_szu1sdr_'))) {
        return min < maxSpace.space && min + itemHeight - protective_field <= maxSpace.space;
      }

      return min < maxSpace.space && min + itemHeight <= maxSpace.space;
    }

    return maxSpace.position > min;
  };
}

function CalcMaximumPosition(slug) {
  if (slug.includes('_dr_')) {
    return rodsCalcMaxPosition;
  }

  if (slug.includes('_szu1sdr_')) {
    return rodsCalcMaxPosition;
  }

  return function defaultCalculate(maxSpace, item, list) {
    const { position } = item;
    const [, max] = position;

    return maxSpace.position <= max;
  };
}

function AccessoriesCalculate(slug) {
  if (slug.includes('_dr_')) {
    return rodsCalcPositionMethod;
  }

  if (slug.includes('_al_bi4k')) {
    return LEDsMethod;
  }

  if (slug.includes('_polst_')) {
    return tShelfMethod;
  }

  return function defaultCalculate(maxSpace) {
    return maxSpace;
  };
}

function CalcEnoughSpace(slug) {
  if (slug.includes('_dr_')) {
    return rodsCalcIsSpaceEnough;
  }

  if (slug.includes('_szu1sdr_')) {
    return rodsCalcIsSpaceEnough;
  }

  if (slug.includes('_polst_')) {
    return polstCalcIsSpaceEnough;
  }

  return function defaultCalculate(maxSpace, item, list) {
    const { protective_field, dimensions } = item;
    const itemHeight = protective_field + dimensions.y;

    const prevItem = list[maxSpace.indexElement];

    const hasTwoRods = list.filter(el => el.slug.includes('_dr_'));
    const hangerRod = list.filter(el => el.slug.includes('_szu1sdr_'));

    if (hasTwoRods.length === 2 || (hasTwoRods.length === 1 && hangerRod.length === 1)) {
      if (item.slug.includes('_al_bi4k')) {
        return true;
      }
      return false;
    }

    if (prevItem && (prevItem.slug.includes('_dr_') || prevItem.slug.includes('_szu1sdr_'))) {
      return itemHeight - protective_field <= maxSpace.space;
    }

    return itemHeight <= maxSpace.space;
  };
}

const recalculatePosition = (maxSpace, item, list) =>
  AccessoriesCalculate(item.slug)(maxSpace, item, list);

const checkMinimalPosition = (maxSpace, item, list) =>
  CalcMinimalPosition(item.slug)(maxSpace, item, list);

const checkMaximumPosition = (maxSpace, item, list) =>
  CalcMaximumPosition(item.slug)(maxSpace, item, list);

const checkEnoughSpace = (maxSpace, item, list) => CalcEnoughSpace(item.slug)(maxSpace, item, list);

function checkForSpace(maxSpace, item, list) {
  const minimalPosition = checkMinimalPosition(maxSpace, item, list);

  const checkMaxPosition = checkMaximumPosition(maxSpace, item, list);

  const checkIsEnoughSpace = checkEnoughSpace(maxSpace, item, list);

  if (minimalPosition && checkMaxPosition && checkIsEnoughSpace) {
    return recalculatePosition(maxSpace, item, list);
  }

  return false;
}

export function checkAvailableSpace(availableSpace, item, list) {
  const { max: maxSpace, spaceFromUp, spaceFromDown } = availableSpace;

  const forSpaceFromDown = checkForSpace(spaceFromDown, item, list);

  if (forSpaceFromDown) {
    return forSpaceFromDown;
  }

  const forMaxSpaceFromDown = checkForSpace(maxSpace, item, list);

  if (forMaxSpaceFromDown) {
    return forMaxSpaceFromDown;
  }

  const forMaxSpaceFromUp = checkForSpace(spaceFromUp, item, list);

  if (forMaxSpaceFromUp) {
    return forMaxSpaceFromUp;
  }

  return false;
}

const calcSpaceToNext = (element, nextElem) => {
  if (nextElem.slug.includes('_dr_')) {
    return nextElem.altitude + element.protective_field - element.actualHeight;
  }

  const spaceToElem = nextElem.altitude - element.actualHeight;

  return spaceToElem;
};

export function calcAvailableSpace(list, wardrobeSize) {
  const initSize = {
    space: 0,
    position: 0,
    indexElement: 0,
  };

  const spaces = {
    wardrobeSize,
    space: wardrobeSize,
    spaceFromUp: initSize,
    spaceFromDown: initSize,
    max: initSize,
  };

  const addToMaxSpace = spacePosition => {
    if (spacePosition.space > spaces.max.space) {
      spaces.max = spacePosition;
    }
  };

  const checkNext = (element, index, listAccessories) => {
    if (listAccessories.length - 1 > index) {
      const nextElem = listAccessories[index + 1];
      const spaceToElem = calcSpaceToNext(element, nextElem);

      addToMaxSpace({
        space: spaceToElem,
        position: element.actualHeight,
        indexElement: index,
      });
    }
  };

  list.forEach((element, index, arr) => {
    spaces.space -= element.protective_field + element.dimensions.y;
    // first
    if (index === 0) {
      spaces.spaceFromDown = {
        space: element.altitude,
        position: 0,
        indexElement: 0,
      };
      addToMaxSpace({
        space: element.altitude,
        position: 0,
        indexElement: 0,
      });
      checkNext(element, index, arr);
    }
    // last
    if (index === arr.length - 1) {
      spaces.spaceFromUp = {
        space: wardrobeSize - element.actualHeight,
        position: element.actualHeight,
        indexElement: index,
      };
    }
    // others
    if (index !== 0 && index !== arr.length - 1) {
      checkNext(element, index, arr);
    }
  });

  const withoutLEDs = list.filter(item => !item.slug.includes('al_bi4k'));

  if (withoutLEDs.length > 1) {
    const last = withoutLEDs[withoutLEDs.length - 1];
    const prevLast = withoutLEDs[withoutLEDs.length - 2];

    if (last.slug.includes('_dr_')) {
      spaces.spaceFromUp = {
        space: last.altitude - prevLast.actualHeight,
        position: prevLast.actualHeight,
        indexElement: withoutLEDs.length - 1,
      };
    }
  }

  if (spaces.space === spaces.wardrobeSize) {
    spaces.spaceFromUp = {
      space: wardrobeSize,
      position: 0,
      indexElement: 0,
    };
  }

  return spaces;
}

export const updateData = update => data => {
  const accessoriesList = [...data];

  accessoriesList.sort((a, b) => {
    return a.altitude - b.altitude;
  });

  update(accessoriesList);
};

export const createAccessoriesList = (accessories, activeWardrobeAccessories) => {
  if (accessories) {
    if (activeWardrobeAccessories) {
      return accessories.map(item => {
        let calculatedValue = 0;
        for (let i = 0; i < activeWardrobeAccessories.length; i += 1) {
          if (item.id === activeWardrobeAccessories[i].id) {
            calculatedValue += 1;
          }
        }
        return {
          ...item,
          value: calculatedValue,
          check: calculatedValue > 0,
        };
      });
    }
    return accessories.map(item => {
      return {
        ...item,
        value: 0,
        check: false,
      };
    });
  }
  return [];
};

export const createItem = (item, selectedAvailableSpace) => {
  const altitude =
    selectedAvailableSpace.position === 0 ? item.position[0] : selectedAvailableSpace.position;

  return {
    ...item,
    uId: `${item.id}__${Math.random()}`,
    check: true,
    altitude,
    actualHeight: calcRealHeight(altitude + item.protective_field + item.dimensions.y),
  };
};

export class Accessories {
  #data = [];

  #wardrobe = null;

  constructor(wardrobe, data) {
    this.#wardrobe = wardrobe;
    this.#data = data || [];
  }

  get list() {
    return this.#data;
  }

  add(item) {
    this.#data.push(item);
    this.#data.sort((a, b) => {
      return a.altitude - b.altitude;
    });

    this.#wardrobe.update(this.#data);
  }

  reset() {
    this.#data = [];
  }
}

export class Wardrobe {
  constructor(wardrobe, zone, updateGlobal) {
    this.updateGlobal = updateGlobal;
    this.wardrobe = wardrobe;
    this.zone = zone;
    this.accessories = new Accessories(this, wardrobe.accessories[zone]);
  }

  get size() {
    return this.wardrobe.dimensions.y;
  }

  get availableSpace() {
    return calcAvailableSpace(this.accessories.list, this.size);
  }

  getAvailableSpace = item => {
    return checkAvailableSpace(this.availableSpace, item, this.accessories.list);
  };

  update = (data = this.accessories.list) => {
    const accessoriesList = [...data];

    this.updateGlobal(accessoriesList);
  };

  addToWardrobe = item => {
    const selectedAvailableSpace = this.getAvailableSpace(item);

    if (selectedAvailableSpace) {
      const newItem = createItem(item, selectedAvailableSpace);

      this.accessories.add(newItem);
    }
  };

  load = (accessoriesList = []) => {
    const LEDs = accessoriesList.filter(item => item.slug.includes('al_bi4k'));
    const withoutLEDs = accessoriesList.filter(item => !item.slug.includes('al_bi4k'));

    withoutLEDs.forEach(this.addToWardrobe);
    LEDs.forEach(this.addToWardrobe);
  };
}
